import { Button } from "@mantine/core";
import React, { useMemo, useState } from "react";
import flat from "flat";
import NewLegacyDocumentUploader from "../../components/NewDocumentUploader/NewDocumentLegacy";
import useMultiFileUpload from "../../hooks/useMultiFileUpload";
import { showNotification } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { putLegacyFlowJson } from "../../utilities/apis/agreements";
import { useSelector } from "react-redux";
import { selectUserData } from "../../redux/selectors";
import { loadingStates } from "../../utilities/utilities";
import { BeatLoader } from "react-spinners";

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const PDF_MIME = "application/pdf";

const isAllowedAgreementFile = (file) => {
  const name = file?.name?.toLowerCase() || "";
  return (
    name.endsWith(".docx") ||
    name.endsWith(".pdf") ||
    file?.type === DOCX_MIME ||
    file?.type === PDF_MIME
  );
};

const LEGACY_DOC_FIELD = "legacyAgreement.document";

const LegacyAgreementForm4 = ({
  uiConfigs,
  setUiConfigs,
  legacyAgreementFormValue,
  setLegacyAgreementFormValue,
  agreementId,
  saveAgreement,
  isEdit = false,
}) => {
  const [selectedFile, setSelectedFile] = useState(false);
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const currentUser = useSelector(selectUserData) || {};

  const flowJsonData = useMemo(() => {
    if (currentUser?.loginAs?.id === 3) {
      return {
        3: [{ userId: currentUser.id, isLegacy: true }],
        4: [],
      };
    }
    if (currentUser?.loginAs?.id === 4) {
      return {
        3: [],
        4: [{ userId: currentUser.id, isLegacy: true }],
      };
    }
    return { 3: [], 4: [] };
  }, [currentUser?.id, currentUser?.loginAs?.id]);

  const baseMultiUpload = useMultiFileUpload({
    loadFromParent: !!agreementId,
    parentId: agreementId,
    parent: "agreement",
  });

  const handleDocxDrop = async (incomingFiles) => {
    const agreementFile = incomingFiles.find(isAllowedAgreementFile);

    if (!agreementFile) {
      showNotification({
        color: "red",
        title: "Invalid file",
        message: "Only .docx or .pdf files are allowed.",
      });
      return;
    }

    if (incomingFiles.length > 1) {
      showNotification({
        color: "red",
        title: "Too many files",
        message: "Please upload only one .docx or .pdf file.",
      });
      return;
    }

    // Replace locally without calling delete API for any existing file
    baseMultiUpload.resetUploadedFiles();
    await baseMultiUpload.addFiles([
      { file: agreementFile, field: LEGACY_DOC_FIELD },
    ]);
  };

  const multiUploadArgs = {
    ...baseMultiUpload,
    files: baseMultiUpload.files.filter(
      (f) =>
        f.field === LEGACY_DOC_FIELD ||
        (!f.field &&
          isAllowedAgreementFile({ name: f.fileName, type: f.mimeType })),
    ),
    addFiles: handleDocxDrop,
  };

  const savePrev = () => {
    setUiConfigs({
      ...uiConfigs,
      currentStep: 1,
    });
  };

  const saveNext = async () => {
    if (!agreementId) {
      showNotification({
        color: "red",
        title: "Agreement",
        message: "Agreement is not saved yet. Please complete previous steps.",
      });
      return;
    }

    const uploadedAgreement =
      baseMultiUpload.files.find(
        (file) =>
          file.uploaded &&
          !file.errored &&
          file.field === LEGACY_DOC_FIELD,
      ) ||
      baseMultiUpload.files.find(
        (file) =>
          file.uploaded &&
          !file.errored &&
          isAllowedAgreementFile({
            name: file.fileName,
            type: file.mimeType,
          }),
      );

    if (!isEdit && (!selectedFile || !uploadedAgreement)) {
      showNotification({
        color: "red",
        title: "Select Agreement",
        message: "Please upload a .docx or .pdf agreement file.",
      });
      return;
    }

    if (isEdit && !uploadedAgreement) {
      showNotification({
        color: "red",
        title: "Agreement document",
        message: "No agreement document is available to save.",
      });
      return;
    }

    if (uploadedAgreement.attaching === loadingStates.LOADING) {
      showNotification({
        color: "red",
        title: "Upload in progress",
        message: "Please wait for the document upload to finish.",
      });
      return;
    }

    setLoader(true);

    try {
      const valuesToSave = {
        ...legacyAgreementFormValue,
        formCompleted: true,
      };
      setLegacyAgreementFormValue(valuesToSave);

      if (!isEdit) {
        await apiWrapWithErrorWithData(
          putLegacyFlowJson(agreementId, flowJsonData),
        );
      }

      const saved = await saveAgreement(flat(valuesToSave));
      if (!saved) {
        return;
      }

      showNotification({
        color: "green",
        title: "Agreement Form",
        message: isEdit ? "Agreement updated successfully." : "Agreement Created",
      });
      navigate(`/app/agreements_manager/agreements/details/${agreementId}`);
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <NewLegacyDocumentUploader
        actionText={null}
        setSelectedFile={setSelectedFile}
        multiUploadArgs={multiUploadArgs}
        docxOnly
        confirmBeforeDelete
        onDropFiles={handleDocxDrop}
      />
      <div className="flex items-center justify-between mt-20">
        <Button
          className="max-w-xs"
          onClick={savePrev}
          style={{
            backgroundColor: "#46BDE1",
          }}
          disabled={uiConfigs.loading === loadingStates.LOADING || loader}
        >
          Previous
        </Button>
        <Button
          className="max-w-xs"
          onClick={saveNext}
          style={{
            backgroundColor: "#46BDE1",
          }}
          disabled={uiConfigs.loading === loadingStates.LOADING || loader}
        >
          {uiConfigs.loading === loadingStates.LOADING || loader ? (
            <BeatLoader color="#fff" size={10} />
          ) : isEdit ? (
            "Save"
          ) : (
            "Finish"
          )}
        </Button>
      </div>
    </>
  );
};

export default LegacyAgreementForm4;
